import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Box
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import { TempoMedioDepartamento } from '../../services/relatorioService';

interface TempoMedioPorDepartamentoProps {
  dados: TempoMedioDepartamento[];
}

const TempoMedioPorDepartamento: React.FC<TempoMedioPorDepartamentoProps> = ({ dados }) => {
  const obterCorTempo = (tempo: number): 'success' | 'warning' | 'error' => {
    if (tempo <= 3) return 'success';
    if (tempo <= 7) return 'warning';
    return 'error';
  };

  const formatarTempo = (dias: string | number): string => {
    const diasNum = typeof dias === 'string' ? parseFloat(dias) : dias;
    if (diasNum === 0) return 'N/A';
    if (diasNum < 1) return `${Math.round(diasNum * 24)}h`;
    if (diasNum === 1) return '1 dia';
    return `${Math.round(diasNum)} dias`;
  };

  if (!dados || dados.length === 0) {
    return (
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <BusinessIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6">Tempo de Conclusão por Departamento</Typography>
          </Box>
          <Typography color="textSecondary">Nenhum dado disponível</Typography>
        </CardContent>
      </Card>
    );
  }

  // Encontrar o melhor e pior departamento
  const melhorDept = dados[0]; // Já vem ordenado por tempo médio ASC
  const piorDept = dados[dados.length - 1];

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <BusinessIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h6">Tempo Médio de Conclusão por Departamento</Typography>
        </Box>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Tempo total desde criação até conclusão das tarefas finalizadas, por departamento
        </Typography>
        
        {/* Destaques */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <Typography variant="caption" color="textSecondary">
              🏆 Mais Rápido
            </Typography>
            <Typography variant="h6" color="success.main">
              {melhorDept.nome}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {formatarTempo(melhorDept.tempoMedio)}
            </Typography>
          </Box>
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <Typography variant="caption" color="textSecondary">
              ⚠️ Precisa Atenção
            </Typography>
            <Typography variant="h6" color="warning.main">
              {piorDept.nome}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {formatarTempo(piorDept.tempoMedio)}
            </Typography>
          </Box>
        </Box>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>Departamento</strong></TableCell>
                <TableCell align="center"><strong>Concluídas</strong></TableCell>
                <TableCell align="center"><strong>Tempo Médio</strong></TableCell>
                <TableCell align="center"><strong>Mais Rápida</strong></TableCell>
                <TableCell align="center"><strong>Mais Lenta</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {dados.map((dept, index) => (
                <TableRow key={dept.departamentoId} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {index === 0 && '🏆'}
                      {index === dados.length - 1 && '⚠️'}
                      <Typography variant="body2" fontWeight="medium">
                        {dept.nome}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Chip 
                      label={dept.totalConcluidas} 
                      size="small" 
                      color="primary" 
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Chip 
                      label={formatarTempo(dept.tempoMedio)}
                      size="small"
                      color={obterCorTempo(parseFloat(dept.tempoMedio.toString()))}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" color="textSecondary">
                      {formatarTempo(dept.maisRapida)}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" color="textSecondary">
                      {formatarTempo(dept.maisLenta)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
};

export default TempoMedioPorDepartamento;
